import { useEffect, useState } from 'react'
import { isAxiosError } from 'axios'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { useUpdateUser, useUserDetails, type UserUpdate } from './api'

/**
 * Correct a user's Emp ID, name, email and mobile (PATCH /v1/users/{id}).
 * Only changed fields are sent. A changed email or mobile is announced to the
 * user's old and new email by the server; a changed Emp ID signs them out.
 */
export function UserDetailsEditor({ userId, disabled }: { userId: string; disabled: boolean }) {
  const details = useUserDetails(userId)
  const update = useUpdateUser()
  const [form, setForm] = useState({ empId: '', fullName: '', email: '', mobile: '' })

  useEffect(() => {
    const d = details.data
    if (d) setForm({ empId: d.empId, fullName: d.fullName, email: d.email ?? '', mobile: d.mobile ?? '' })
  }, [details.data])

  const d = details.data
  const change: UserUpdate = {}
  if (d) {
    if (form.empId.trim().toUpperCase() !== d.empId) change.empId = form.empId.trim().toUpperCase()
    if (form.fullName.trim() !== d.fullName) change.fullName = form.fullName.trim()
    if (form.email.trim() !== (d.email ?? '')) change.email = form.email.trim()
    if (form.mobile.trim() !== (d.mobile ?? '')) change.mobile = form.mobile.trim()
  }
  const dirty = Object.keys(change).length > 0

  function onSave(e: React.FormEvent) {
    e.preventDefault()
    if (!dirty || !d) return
    if (
      change.empId &&
      !window.confirm(
        `Change Emp ID ${d.empId} to ${change.empId}? It is their login ID: they are signed out now and must ` +
          'sign in with the new one. Allocation files must use the new ID from now on.',
      )
    ) {
      return
    }
    update.mutate({ userId, change })
  }

  // The server's reason leads with a code ("mobile_taken: …"); show the sentence.
  const error = update.isError
    ? isAxiosError(update.error)
      ? String(update.error.response?.data?.detail ?? update.error.response?.data?.message ?? 'Could not save.')
          .replace(/^[a-z_]+:\s*/, '')
      : 'Could not save.'
    : null

  if (details.isPending) return <p className="text-2xs text-muted-foreground">Loading details…</p>
  if (details.isError || !d) return <p className="text-2xs text-negative">Couldn't load this user's details.</p>

  return (
    <form onSubmit={onSave} className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-4">
        <div>
          <Label htmlFor={`emp-${userId}`}>Emp ID</Label>
          <Input
            id={`emp-${userId}`}
            className="font-mono"
            value={form.empId}
            disabled={disabled}
            onChange={(e) => setForm((f) => ({ ...f, empId: e.target.value.toUpperCase() }))}
          />
        </div>
        <div>
          <Label htmlFor={`name-${userId}`}>Full name</Label>
          <Input
            id={`name-${userId}`}
            value={form.fullName}
            disabled={disabled}
            onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor={`email-${userId}`}>Email</Label>
          <Input
            id={`email-${userId}`}
            type="email"
            value={form.email}
            disabled={disabled}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div>
          <Label htmlFor={`mobile-${userId}`}>Mobile</Label>
          <Input
            id={`mobile-${userId}`}
            value={form.mobile}
            placeholder="+91…"
            disabled={disabled}
            onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" disabled={disabled || !dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save details'}
        </Button>
        {error && <span className="text-2xs text-negative">{error}</span>}
        {update.isSuccess && !dirty && <span className="text-2xs text-positive">Saved.</span>}
        <span className="text-2xs text-muted-foreground">
          {disabled
            ? 'An offboarded user cannot be edited.'
            : 'A changed email or mobile is emailed to the old and the new address. Clear a field to remove it.'}
        </span>
      </div>
    </form>
  )
}
